import { useEffect, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const toDisplay = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
const toDate = (iso) => (iso ? parse(iso, "yyyy-MM-dd", new Date()) : undefined);

function parseTyped(text) {
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(text.trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return isValid(d) && d.getDate() === Number(m[1]) ? format(d, "yyyy-MM-dd") : null;
}

export const DateField = ({ value, onChange, testId, className, clearable = true }) => {
  const [text, setText] = useState(toDisplay(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(toDisplay(value)); }, [value]);

  const commit = () => {
    if (!text.trim()) { if (value) onChange(""); return; }
    const iso = parseTyped(text);
    if (iso) { if (iso !== value) onChange(iso); setText(toDisplay(iso)); } else setText(toDisplay(value));
  };

  return (
    <div className={cn("relative flex items-center w-40", className)}>
      <Input value={text} placeholder="gg.aa.yyyy" inputMode="numeric" maxLength={10}
             onChange={(e) => setText(e.target.value)} onBlur={commit}
             onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
             className="pr-14 bg-white" data-testid={testId} />
      <div className="absolute right-1 flex items-center">
        {clearable && value && (
          <button type="button" onClick={() => onChange("")} className="p-1 text-slate-400 hover:text-slate-700"
                  aria-label="Tarihi temizle" data-testid={testId ? `${testId}-clear` : undefined}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-slate-500"
                    aria-label="Takvimi aç" data-testid={testId ? `${testId}-calendar` : undefined}>
              <CalendarDays className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" locale={tr} weekStartsOn={1} selected={toDate(value)}
                      defaultMonth={toDate(value)} initialFocus
                      onSelect={(d) => { if (d) onChange(format(d, "yyyy-MM-dd")); setOpen(false); }} />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
